import { useQuery } from '@tanstack/react-query';
import api from 'api';
import { getMixpanelErrorProps } from 'axiosUtils';
import { FIRST_PAGE } from 'data/global';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import useInvalidateQueryOnUnmount from 'hooks/useInvalidateQueryOnUnmount';
import { EnterpriseDataRoomSuggestedActions } from 'models/EnterpriseSuggestedActions';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import { PaginatedData } from 'models/PaginatedData';
import mixpanelService from 'services/mixpanelService';

const LIMIT = '10';
export const ENTERPRISE_SUGGESTED_ACTIONS_QUERY_KEY =
  'enterprise-suggested-actions';

type SuggestedActionsFilters = {
  hasSuggestedActions?: boolean;
  actionTypes?: string[];
};

export default function useEnterpriseSuggestedActionsQuery(
  enterpriseId?: string,
  dealRoomIds?: string[],
  page: string = FIRST_PAGE,
  filters: SuggestedActionsFilters = {},
  sort?: string,
) {
  const getMixpanelProps = useGetMixpanelProps();

  const { hasSuggestedActions, actionTypes } = filters;

  const query = useQuery<PaginatedData<EnterpriseDataRoomSuggestedActions>>({
    queryKey: [
      ENTERPRISE_SUGGESTED_ACTIONS_QUERY_KEY,
      { enterpriseId, dealRoomIds, page, hasSuggestedActions, actionTypes, sort },
    ],
    queryFn: async () => {
      const { data } =
        await api.enterpriseSuggestedActions.getEnterpriseSuggestedActions(
          enterpriseId,
          hasSuggestedActions,
          actionTypes,
          dealRoomIds,
          { $page: page, $limit: LIMIT, ...(sort && { $sort: sort }) },
        );

      return data;
    },
    onError: (error) =>
      mixpanelService.errorViewed(
        getMixpanelProps(getMixpanelErrorProps(error)) as ErrorViewedProps,
      ),
    enabled: !!enterpriseId,
  });

  useInvalidateQueryOnUnmount(ENTERPRISE_SUGGESTED_ACTIONS_QUERY_KEY);

  return query;
}
